import { CartAddEvent } from '@theme/events';
import { resolveVariant, optionAvailable, reconcileSelection, validQuantity } from '@theme/casa-commerce-model';
import { openAddedDialog } from '@theme/casa-added-dialog';

const initialized = new WeakSet();
const addedTimers = new WeakMap();
const ADD_BUTTON_FEEDBACK_MS = 5000;
const MOBILE_SIZE_QUERY = '(max-width: 767px)';
let cartQueue = Promise.resolve();
const enqueue = task => { const result = cartQueue.then(task); cartQueue = result.catch(() => {}); return result; };

async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error('Cart read failed');
  return response.json();
}
function notifyCart(form, cart, sections) {
  form.dispatchEvent(new CartAddEvent(cart, form.getAttribute('id'), {
    source: 'casa-product-form', productId: form.dataset.casaProductId,
    itemCount: cart.item_count, sections,
  }));
}
function isMobileSize() {
  return window.matchMedia(MOBILE_SIZE_QUERY).matches;
}
function needsSizePrompt(sizeSelect, available) {
  return isMobileSize() && Boolean(sizeSelect) && sizeSelect.value === '' && available;
}
function idleAddLabel(button) {
  return button.dataset.casaNeedsSize === 'true' ? button.dataset.casaChooseSize : button.dataset.casaAddLabel;
}
function openSizeSelect(select) {
  if (typeof select.showPicker !== 'function') {
    select.focus();
    return;
  }
  try { select.showPicker(); } catch { select.focus(); }
}
function restoreAddButton(button) {
  const timer = addedTimers.get(button);
  if (timer) clearTimeout(timer);
  addedTimers.delete(button);
  button.removeAttribute('data-added');
  button.removeAttribute('data-status');
  if (button.dataset.casaAddLabel) button.textContent = idleAddLabel(button);
}
function syncSizePrompt(form) {
  const button = form.querySelector('[data-casa-add-button]');
  const sizeSelect = form.querySelector('[data-casa-size-select]');
  const variants = JSON.parse(form.querySelector('[data-casa-variants]').textContent);
  const available = variants.some(item => item.available);
  button.dataset.casaNeedsSize = needsSizePrompt(sizeSelect, available) ? 'true' : 'false';
  if (button.dataset.status || button.dataset.added || button.dataset.loading) {
    if (button.dataset.casaNeedsSize !== 'true' && button.textContent === button.dataset.casaChooseSize) {
      button.textContent = button.dataset.casaAddLabel;
    }
    return;
  }
  restoreAddButton(button);
}
function markAddButton(button, label) {
  restoreAddButton(button);
  button.dataset.added = 'true';
  button.textContent = label;
  addedTimers.set(button, setTimeout(() => restoreAddButton(button), ADD_BUTTON_FEEDBACK_MS));
}
function showStatusOnButton(button, message) {
  const timer = addedTimers.get(button);
  if (timer) clearTimeout(timer);
  button.removeAttribute('data-added');
  button.textContent = message;
  button.removeAttribute('data-status');
  void button.offsetWidth;
  button.setAttribute('data-status', 'true');
  const statusTimer = setTimeout(() => restoreAddButton(button), ADD_BUTTON_FEEDBACK_MS);
  statusTimer.unref?.();
  addedTimers.set(button, statusTimer);
}
function setStatus(status, message, visual = true, button) {
  status.textContent = message;
  status.classList.toggle('casa-sr-only', Boolean(message));
  if (visual && button && message) showStatusOnButton(button, message);
}
function clearLoading(button) {
  button.removeAttribute('data-loading');
  button.removeAttribute('aria-busy');
}
function confirmAdd(form, button, status, variant, quantity) {
  setStatus(status, form.dataset.casaAdded, false);
  // The 5-second button label is only a fallback when the modal is not rendered.
  if (!openAddedDialog({ trigger: button, form, variant, quantity })) markAddButton(button, form.dataset.casaAdded);
}
function initializeForm(form) {
  if (initialized.has(form)) return;
  const variants = JSON.parse(form.querySelector('[data-casa-variants]').textContent);
  const native = form.querySelector('[data-casa-native-variant]');
  const select = form.querySelector('[name="id"]');
  const controls = form.querySelector('[data-casa-option-controls]');
  const quantity = form.querySelector('[name="quantity"]');
  const button = form.querySelector('[data-casa-add-button]');
  const status = form.querySelector('[data-casa-product-status]');
  button.dataset.casaAddLabel = button.textContent;
  button.dataset.casaChooseSize = form.dataset.casaChooseSize || '';
  const inputs = [...controls.querySelectorAll('[data-casa-option-position]')];
  const radios = inputs.filter(input => input.type === 'radio');
  const sizeSelect = form.querySelector('[data-casa-size-select]');
  const sizeField = form.querySelector('[data-casa-size-field]');
  const selects = inputs.filter(input => input.tagName === 'SELECT');
  const selectPositions = selects.map(input => Number(input.dataset.casaOptionPosition));
  const getOptions = () => {
    const options = Array(variants[0]?.options.length || 0).fill(null);
    inputs.forEach(input => { if (input.type !== 'radio' || input.checked) options[Number(input.dataset.casaOptionPosition)] = input.value; });
    return options;
  };
  const update = event => {
    if (event?.target === sizeSelect) {
      radios.forEach(input => { input.checked = !input.disabled && input.value === sizeSelect.value; });
    }
    const state = reconcileSelection(variants, getOptions(), selectPositions);
    selects.forEach(input => {
      const index = Number(input.dataset.casaOptionPosition);
      [...input.options].forEach(option => {
        option.disabled = !state.choices[index].includes(option.value);
        option.hidden = option.disabled;
      });
      input.value = state.options[index] || '';
    });
    radios.forEach(input => {
      const index = Number(input.dataset.casaOptionPosition);
      input.disabled = !optionAvailable(variants, state.options, index, input.value);
      input.checked = !input.disabled && state.options[index] === input.value;
    });
    if (sizeSelect) {
      [...sizeSelect.options].forEach(option => {
        option.disabled = Boolean(option.value) && !radios.some(input => input.value === option.value && !input.disabled);
      });
      sizeSelect.value = radios.find(input => input.checked)?.value || '';
    }
    const { variant, preview } = state;
    select.value = variant?.id || '';
    if (preview) {
      // Money markup comes from Shopify's server-side money filter.
      form.querySelector('[data-casa-price]').innerHTML = preview.price;
      const compare = form.querySelector('[data-casa-compare]');
      compare.innerHTML = preview.compare || ''; compare.hidden = !preview.compare;
      quantity.min = preview.quantity_rule.min;
      quantity.step = preview.quantity_rule.increment;
      if (preview.quantity_rule.max) quantity.max = preview.quantity_rule.max; else quantity.removeAttribute('max');
      if (!validQuantity(quantity.value, preview.quantity_rule)) quantity.value = preview.quantity_rule.min;
      if (event && preview.image) {
        const image = form.closest('.casa-product')?.querySelector('.casa-product__stage img');
        if (image) {
          image.removeAttribute('srcset');
          image.src = preview.image;
          image.alt = preview.image_alt;
        }
      }
    }
    form.closest('.casa-product')?.querySelectorAll('[data-casa-product-link]').forEach(link => {
      const url = new URL(link.href);
      if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
      link.href = url.href;
    });
    const available = variants.some(item => item.available);
    button.disabled = form.dataset.casaBusy === 'true' || !available;
    button.dataset.casaNeedsSize = needsSizePrompt(sizeSelect, available) ? 'true' : 'false';
    restoreAddButton(button);
    setStatus(status, '');
  };
  syncSizeValidity(form);
  controls.addEventListener('change', update);
  sizeSelect?.addEventListener('change', update);
  select.required = false;
  native.hidden = true;
  controls.hidden = false;
  if (sizeField) sizeField.hidden = false;
  update();
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (form.dataset.casaBusy === 'true') return;
    if (needsSizePrompt(sizeSelect, variants.some(item => item.available))) {
      button.dataset.casaNeedsSize = 'true';
      restoreAddButton(button);
      openSizeSelect(sizeSelect);
      return;
    }
    restoreAddButton(button);
    const variant = resolveVariant(variants, getOptions());
    if (!variant?.available) return;
    if (!validQuantity(quantity.value, variant.quantity_rule)) { setStatus(status, form.dataset.casaQuantityError, true, button); return; }
    const requestedQuantity = Number(quantity.value);
    button.disabled = true;
    form.dataset.casaBusy = 'true';
    button.setAttribute('data-loading', 'true');
    button.setAttribute('aria-busy', 'true');
    setStatus(status, '');
    enqueue(async () => {
      let committed = false;
      try {
        const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: requestedQuantity }], sections, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          clearLoading(button);
          setStatus(status, data.description || data.message || form.dataset.casaCartError, true, button);
          // Shopify can partially add on a 422; a read reconciles the native cart.
          try { notifyCart(form, await readCart()); } catch { /* Retain the server error. */ }
          return;
        }
        committed = true;
        // Confirm before the cart read so the modal does not wait on a second round trip.
        confirmAdd(form, button, status, variant, requestedQuantity);
        notifyCart(form, await readCart(), data.sections);
      } catch {
        // A lost response may follow a committed add. Never repeat the mutation.
        if (!committed) {
          clearLoading(button);
          setStatus(status, form.dataset.casaCartUncertain, true, button);
        }
        try { notifyCart(form, await readCart()); } catch {
          restoreAddButton(button);
          setStatus(status, form.dataset.casaCartUncertain, true, button);
        }
      } finally {
        delete form.dataset.casaBusy;
        button.disabled = false;
        clearLoading(button);
      }
    });
  });
  initialized.add(form);
}
function syncSizeValidity(form) {
  const mobile = isMobileSize();
  form.querySelectorAll('input[type="radio"][name="size"]').forEach(input => { input.required = !mobile; });
  const dropdown = form.querySelector('[data-casa-size-select]');
  if (dropdown) dropdown.required = false;
}
function initialize(root = document) {
  root.querySelectorAll('[data-casa-product-form]').forEach(form => {
    try { initializeForm(form); } catch (error) {
      form.querySelector('[data-casa-native-variant]').hidden = false;
      form.querySelector('[name="id"]').required = true;
      form.querySelector('[data-casa-option-controls]').hidden = true;
      form.querySelectorAll('input[type="radio"]').forEach(input => { input.required = false; });
      const sizeField = form.querySelector('[data-casa-size-field]');
      if (sizeField) sizeField.hidden = true;
      const dropdown = form.querySelector('[data-casa-size-select]');
      if (dropdown) dropdown.required = false;
      console.error('Casa form initialization failed; native selection remains available', error);
    }
  });
  const story = document.querySelector('[data-casa-story]');
  if (story?.id) document.querySelectorAll('[data-casa-story-link]').forEach(link => { link.href = `#${story.id}`; });
}
document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-casa-open-size-guide]');
  if (trigger) document.getElementById('CasaSizeGuide')?.showModal();
  if (event.target.closest('[data-casa-close-size-guide]')) document.getElementById('CasaSizeGuide')?.close();
});
document.addEventListener('shopify:section:load', event => initialize(event.target));
initialize();
window.matchMedia(MOBILE_SIZE_QUERY).addEventListener('change', () => {
  document.querySelectorAll('[data-casa-product-form]').forEach(form => {
    if (!initialized.has(form)) return;
    syncSizeValidity(form);
    syncSizePrompt(form);
  });
});
